/**
 * Every route a workflow step is allowed to point at.
 *
 * Taken from the real app route tree under
 * `apps/web/app/(saas)/app/(organizations)/[organizationSlug]/properties/[propertyId]/`
 * (property-relative) plus the organization pages (`org:` prefix). Only static
 * segments are listed: a step never deep-links into a route with a dynamic
 * parameter, because the runner has no id to fill it with.
 *
 * A third form, `location:/…`, is the *same* property-relative page resolved
 * against a LOCATION child instead of the property the runner is on — W7's
 * localized competitor set and prompt review live on the market, not on the
 * parent. It carries no separate allowlist: the suffix is validated against
 * `PROPERTY_WORKFLOW_ROUTES` below, because a location child is a property and
 * has exactly the same pages.
 *
 * `catalog.test.ts` asserts every step route is a member, which is what turns a
 * typo into a red test rather than a 404 behind an "Open the page" button.
 */

/** Pages under `…/properties/<id>`; also the suffixes a `location:` route may use. */
export const PROPERTY_WORKFLOW_ROUTES = [
	// Property home
	"/",
	// Reports and identity
	"/ai-reports",
	"/brand-snapshot",
	"/key-messages",
	"/competitors",
	// Prompts
	"/discovery-queries",
	"/discovery-queries/new",
	// Projects and timeline
	"/projects",
	"/projects/new",
	"/annotations",
	// Brand dashboard drill-downs
	"/dashboard/competitors",
	"/dashboard/consistency",
	"/dashboard/consultations",
	"/dashboard/earned-media",
	"/dashboard/goals",
	"/dashboard/locations",
	"/dashboard/messages",
	"/dashboard/publishers",
	"/dashboard/seo",
	"/dashboard/site-audit",
	"/dashboard/visibility",
	// Legacy metric pages still reachable from the sidebar
	"/ai-visibility-metrics",
	"/ai-visibility-rankings",
	// Earned media
	"/citation-intelligence",
	"/coverage-groups",
	"/pitch-lists",
	"/placements",
	"/placements/new",
	"/publisher-lookup",
	// Content and site
	"/citation-optimizer",
	"/faq-generator",
	"/pages",
	"/site-audit",
	"/recrawl",
	// Locations
	"/locations",
	// Traffic
	"/ai-analytics",
	"/analytics",
	"/analytics-dashboard",
	"/traffic-control",
	// Settings
	"/settings",
	// Workflows themselves (PR 4)
	"/workflows",
] as const;

/** Pages under `/app/<slug>`. */
export const ORG_WORKFLOW_ROUTES = ["org:/", "org:/workflows"] as const;

export const WORKFLOW_ROUTES = [
	...PROPERTY_WORKFLOW_ROUTES,
	...ORG_WORKFLOW_ROUTES,
] as const;

/** `org:/settings` → the organization base path. */
export const ORG_ROUTE_PREFIX = "org:";
/** `location:/competitors` → the bound LOCATION child's own page. */
export const LOCATION_ROUTE_PREFIX = "location:";

export type WorkflowPropertyRoute = (typeof PROPERTY_WORKFLOW_ROUTES)[number];
export type WorkflowRoute =
	| (typeof WORKFLOW_ROUTES)[number]
	| `location:${WorkflowPropertyRoute}`;

/**
 * Split a step route into the part the allowlist governs and the query string
 * it may carry.
 *
 * A route names a *page*, and some pages have more than one view behind a query
 * parameter — the organization dashboard's Clients and Prospects tabs are one
 * page, and W4's steps all happen on the Prospects one (`org:/?tab=prospects`).
 * Putting the tab in the allowlist would make it a second page, and every
 * future tab a third; validating the path and carrying the query through keeps
 * the allowlist about pages while the step still lands where it means to.
 *
 * A `#fragment` rides along the same way: property settings read
 * `?tab=notifications` to pick the tab and `#wrong-domain-alerts` to scroll to
 * one card on it. `query` is everything from the first `?` or `#` onward.
 */
export function splitRouteQuery(route: string): {
	path: string;
	query: string;
} {
	const index = route.search(/[?#]/);
	return index === -1
		? { path: route, query: "" }
		: { path: route.slice(0, index), query: route.slice(index) };
}

export function isWorkflowRoute(route: string): boolean {
	const { path } = splitRouteQuery(route);
	if (path.startsWith(LOCATION_ROUTE_PREFIX)) {
		// Same pages, different base — a location child is a property.
		return (PROPERTY_WORKFLOW_ROUTES as readonly string[]).includes(
			path.slice(LOCATION_ROUTE_PREFIX.length),
		);
	}
	return (WORKFLOW_ROUTES as readonly string[]).includes(path);
}
